// Tính toán cho Dashboard công việc: mọi khối trên trang đều đi qua bucketOf() để số liệu khớp nhau
import dayjs from 'dayjs';
import { MEMBERS, PROJECTS, DEPARTMENTS, type Repeat, type WorkTask } from '../WorkList/data';

/** Mỗi công việc thuộc đúng 1 nhóm trạng thái */
export type Bucket = 'on_time' | 'late' | 'overdue' | 'doing' | 'review' | 'failed';

export const BUCKETS: Bucket[] = ['on_time', 'late', 'overdue', 'doing', 'review', 'failed'];

export const BUCKET_META: Record<Bucket, { label: string; short: string; color: string; hint: string }> = {
  on_time: { label: 'Hoàn thành đúng hạn', short: 'Đúng hạn', color: '#22c55e', hint: 'Đã xong, ngày xong ≤ hạn chót (hoặc không có hạn)' },
  late: { label: 'Hoàn thành muộn', short: 'Muộn', color: '#eab308', hint: 'Đã xong nhưng sau hạn chót' },
  overdue: { label: 'Quá hạn', short: 'Quá hạn', color: '#ef4444', hint: 'Chưa xong và đã qua hạn chót' },
  doing: { label: 'Đang xử lý', short: 'Đang làm', color: '#3b82f6', hint: 'Chưa xong, còn trong hạn' },
  review: { label: 'Chờ duyệt', short: 'Chờ duyệt', color: '#a855f7', hint: 'Người làm đã báo xong, chờ người giao đánh giá' },
  failed: { label: 'Không đạt', short: 'Không đạt', color: '#64748b', hint: 'Đóng lại vì không đạt / không thực hiện' },
};

export const today = () => dayjs().format('YYYY-MM-DD');

export function bucketOf(t: WorkTask, now = today()): Bucket {
  if (t.status === 'done') return t.dueAt && t.doneAt && t.doneAt > t.dueAt ? 'late' : 'on_time';
  if (t.status === 'failed') return 'failed';
  if (t.status === 'review') return 'review';
  return t.dueAt && t.dueAt < now ? 'overdue' : 'doing';
}

export const isOpen = (b: Bucket) => b === 'overdue' || b === 'doing' || b === 'review';

export interface Filters {
  from: string;
  to: string;
  basis: 'created' | 'due';
  departmentId?: string;
  projectId?: string;
  scope: 'all' | 'main';
  bucket?: Bucket;
}

/** Lọc mọi điều kiện trừ khoảng ngày; việc đã huỷ không tính vào báo cáo (dùng cho biểu đồ xu hướng cần dữ liệu trước kỳ) */
export function filterBase(tasks: WorkTask[], f: Filters, now = today()) {
  return tasks.filter(t =>
    t.status !== 'cancelled'
    && (!f.departmentId || t.departmentId === f.departmentId)
    && (!f.projectId || t.projectId === f.projectId)
    && (f.scope === 'all' || !t.parentId)
    && (!f.bucket || bucketOf(t, now) === f.bucket));
}

export function inRange(t: WorkTask, f: Filters) {
  const d = f.basis === 'created' ? t.createdAt : t.dueAt;
  return !!d && d >= f.from && d <= f.to;
}

export const filterTasks = (tasks: WorkTask[], f: Filters, now = today()) => filterBase(tasks, f, now).filter(t => inRange(t, f));

export type Counts = Record<Bucket, number> & { total: number; unassigned: number; noDue: number; reviewOverdue: number };

export function countOf(tasks: WorkTask[], now = today()): Counts {
  const c: Counts = { on_time: 0, late: 0, overdue: 0, doing: 0, review: 0, failed: 0, total: 0, unassigned: 0, noDue: 0, reviewOverdue: 0 };
  tasks.forEach(t => {
    const b = bucketOf(t, now);
    c[b] += 1;
    c.total += 1;
    if (!t.assigneeId && isOpen(b)) c.unassigned += 1;
    if (!t.dueAt) c.noDue += 1;
    if (b === 'review' && t.dueAt && t.dueAt < now) c.reviewOverdue += 1;
  });
  return c;
}

export const doneCount = (c: Counts) => c.on_time + c.late;
export const openCount = (c: Counts) => c.overdue + c.doing + c.review;
export const pct = (a: number, b: number) => (b ? Math.round((a / b) * 1000) / 10 : 0);

export interface GroupRow extends Counts { key: string; name: string; sub?: string; color?: string }

export type GroupBy = 'project' | 'department' | 'assignee' | 'creator';

export function groupTasks(tasks: WorkTask[], by: GroupBy, now = today()): GroupRow[] {
  const keyOf = (t: WorkTask) => (by === 'project' ? t.projectId : by === 'department' ? t.departmentId : by === 'assignee' ? t.assigneeId ?? '' : t.creatorId);
  const map = new Map<string, WorkTask[]>();
  tasks.forEach(t => { const k = keyOf(t); map.set(k, [...(map.get(k) ?? []), t]); });
  return Array.from(map.entries()).map(([key, list]) => {
    const c = countOf(list, now);
    if (by === 'project') {
      const p = PROJECTS.find(x => x.id === key);
      return { ...c, key, name: p?.name ?? key, sub: p?.type === 'team' ? 'Phòng ban' : 'Dự án' };
    }
    if (by === 'department') return { ...c, key, name: DEPARTMENTS.find(d => d.id === key)?.name ?? key };
    const m = MEMBERS.find(x => x.id === key);
    if (!m) return { ...c, key, name: 'Chưa giao người làm', color: '#cbd5e1' };
    return { ...c, key, name: m.name, sub: DEPARTMENTS.find(d => d.id === m.departmentId)?.name, color: m.color };
  }).sort((a, b) => b.total - a.total);
}

export const tasksOfGroup = (tasks: WorkTask[], by: GroupBy, key: string) => tasks.filter(t =>
  (by === 'project' ? t.projectId : by === 'department' ? t.departmentId : by === 'assignee' ? t.assigneeId ?? '' : t.creatorId) === key);

/** Thành viên làm tốt: nhiều việc xong đúng hạn nhất, tối thiểu 3 việc được giao.
 *  Tỷ lệ = đúng hạn / việc đã đến hạn hoặc đã xong (không phạt việc còn trong hạn) — cùng cách tính cột % đúng hạn */
export function topPerformers(tasks: WorkTask[], limit = 5, now = today()) {
  return groupTasks(tasks.filter(t => t.assigneeId), 'assignee', now)
    .filter(r => r.total >= 3)
    .map(r => ({ ...r, rate: pct(r.on_time, r.on_time + r.late + r.overdue + r.failed) }))
    .sort((a, b) => b.on_time - a.on_time || b.rate - a.rate)
    .slice(0, limit);
}

export interface RoutineRow { key: string; name: string; departmentId: string; repeat?: Repeat; due: number; onTime: number; late: number; missed: number; rate: number | null; tasks: WorkTask[] }

/**
 * Tuân thủ quy trình định kỳ (khử khuẩn, hộp chống sốc, nhiệt độ tủ…): gom theo mẫu quy trình (nhiều phòng dùng chung một mẫu
 * tính chung), việc tự tạo không theo mẫu thì theo chuỗi; tính trên các lần đã đến hạn.
 * Bỏ lỡ = quá hạn chưa làm hoặc đóng "không đạt". Xếp tỷ lệ thấp lên đầu để quản lý thấy ngay.
 */
export function routineCompliance(tasks: WorkTask[], now = today()): RoutineRow[] {
  const map = new Map<string, WorkTask[]>();
  tasks.filter(t => t.repeat || t.seriesId).forEach(t => { const k = t.templateId ?? t.seriesId ?? t.id; map.set(k, [...(map.get(k) ?? []), t]); });
  return Array.from(map.entries()).map(([key, list]) => {
    const c = countOf(list, now);
    const due = c.on_time + c.late + c.overdue + c.failed;
    const last = [...list].sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
    return { key, name: last.name, departmentId: last.departmentId, repeat: last.repeat, due, onTime: c.on_time, late: c.late, missed: c.overdue + c.failed, rate: due ? pct(c.on_time, due) : null, tasks: list };
  }).sort((a, b) => (a.rate ?? 101) - (b.rate ?? 101) || b.due - a.due);
}

export interface TrendPoint { label: string; created: number; done: number; open: number }

/** Xu hướng trong kỳ: tạo mới / hoàn thành mỗi ngày (hoặc tuần nếu kỳ > 62 ngày) và số việc còn tồn cuối mỗi mốc */
export function trend(base: WorkTask[], from: string, to: string): TrendPoint[] {
  const start = dayjs(from);
  const end = dayjs(to);
  const weekly = end.diff(start, 'day') > 62;
  const points: TrendPoint[] = [];
  let cur = weekly ? start.startOf('week') : start;
  while (!cur.isAfter(end)) {
    const s = cur.format('YYYY-MM-DD');
    const next = cur.add(1, weekly ? 'week' : 'day');
    const e = next.subtract(1, 'day').format('YYYY-MM-DD');
    points.push({
      label: weekly ? `${cur.format('DD/MM')}–${next.subtract(1, 'day').format('DD/MM')}` : cur.format('DD/MM'),
      created: base.filter(t => t.createdAt >= s && t.createdAt <= e).length,
      done: base.filter(t => t.doneAt && t.doneAt >= s && t.doneAt <= e).length,
      open: base.filter(t => t.createdAt <= e && t.status !== 'failed' && (!t.doneAt || t.doneAt > e)).length,
    });
    cur = next;
  }
  return points;
}

export type Quadrant = 'do' | 'plan' | 'delegate' | 'drop';

export const QUADRANT_META: Record<Quadrant, { label: string; hint: string; cls: string }> = {
  do: { label: 'Làm ngay', hint: 'Quan trọng · Khẩn cấp', cls: 'bg-red-50 text-red-600 border-red-100 dark:bg-red-900/20 dark:border-red-800' },
  plan: { label: 'Lên lịch', hint: 'Quan trọng · Không khẩn', cls: 'bg-green-50 text-green-700 border-green-100 dark:bg-green-900/20 dark:border-green-800' },
  delegate: { label: 'Giao lại', hint: 'Khẩn cấp · Không quan trọng', cls: 'bg-amber-50 text-amber-700 border-amber-100 dark:bg-amber-900/20 dark:border-amber-800' },
  drop: { label: 'Để sau', hint: 'Không quan trọng · Không khẩn', cls: 'bg-slate-50 text-slate-500 border-slate-100 dark:bg-gray-700 dark:border-gray-600' },
};

export const quadrantOf = (t: WorkTask): Quadrant => (t.important ? (t.urgent ? 'do' : 'plan') : t.urgent ? 'delegate' : 'drop');
